import { defineComponent as _defineComponent } from 'vue'
import {
	ref,
	Ref,
	nextTick,
	watch,
	onMounted,
	computed,
	ComputedRef,
} from "vue";
import store from "@/vue/planning/store/store";
import PrestationComp from "@/vue/planning/components/result/prestation.vue";
import OopsBanner2 from "@/vue/planning/components/oopsBanner2.vue";
import {
	defaultNumberOfPrestationsToShow_Desktop,
	additionalNumberOfPrestationsToShow_Desktop,
	defaultNumberOfPrestationsToShow_Mobile,
	additionalNumberOfPrestationsToShow_Mobile,
} from "@/config/plannerConfig";
import BlueBtn from "@/vue/common/BlueBtn.vue";

// Get cookies
import { getCookie } from "@/utils/utils";

// types
import { Prestation } from "@/types/index";

// computed
import availableTransportPackCodes from "@/vue/planning/computed/availableTransportPackCodes";

// Tracking
import trackViewItem from "@/tracking/trackItems";

// refs

export default /*#__PURE__*/_defineComponent({
  __name: 'result',
  setup(__props) {

const prestationsList = ref();
const isAnimating: Ref<boolean> = ref(false);
const queue: Ref<Array<any>> = ref([]);
const prestations: Ref<Array<Prestation>> = ref([]);
const additionalPrestationsNum: Ref<number> = ref(0);
const company = getCookie("company");

// computed
const defaultNumberOfPrestationsToShow = computed(() => {
	if (store.state.screenType === "mobile") {
		return defaultNumberOfPrestationsToShow_Mobile;
	}

	return defaultNumberOfPrestationsToShow_Desktop;
});
const additionalNumberOfPrestationsToShow = computed(() => {
	if (store.state.screenType === "mobile") {
		return additionalNumberOfPrestationsToShow_Mobile;
	}
	return additionalNumberOfPrestationsToShow_Desktop;
});
const stepNum = computed(() => {
	if (
		availableTransportPackCodes.value.length > 0 &&
		store.state.screenType === "desktop"
	)
		return "3. ";
	return "2. ";
});
const prestationsToShow: ComputedRef<Array<Prestation>> = computed(() => {
	let result: Array<Prestation> = [];
	if (store.getters.locale === "fr_FR_CSE" && company === "cgos") {
		const prestationsWithCapacityLimitation = prestations.value.filter(
			(prestation) => prestation.capacity <= 8
		);

		result = prestationsWithCapacityLimitation.slice(
			0,
			defaultNumberOfPrestationsToShow.value + additionalPrestationsNum.value
		);
	} else {
		result = prestations.value.slice(
			0,
			defaultNumberOfPrestationsToShow.value + additionalPrestationsNum.value
		);
	}

	return result;
});
const isShowingAllPrestations = computed(() => {
	return (
		store.getters.prestations.length <
		defaultNumberOfPrestationsToShow.value + additionalPrestationsNum.value
	);
});

// methods
const scrollToPlannerStart = () => {
	window.dispatchEvent(
		new CustomEvent("smoothScrollToSelector", {
			detail: {
				selector: "#tf-planning",
			},
		})
	);
};

const getEmptyPrestations = (): Array<Prestation> => {
	// controls the number of placeholders to show during loading
	if (store.state.screenType === "mobile") {
		return [];
	} else if (store.state.screenType === "tablet") {
		return [{}];
	} else {
		return [{}, {}, {}];
	}
};

const updateAnimations = (): void => {
	if (queue.value.length === 0) {
		isAnimating.value = false;
		return;
	}

	const newPrestations = queue.value.shift(); // pops object from beginning of array
	queue.value = [...queue.value];

	if (
		prestations.value.length === 0 ||
		Object.keys(prestations.value[0]).length === 0
	) {
		if (
			newPrestations.length === 0 ||
			Object.keys(newPrestations[0]).length === 0
		) {
			updateAnimations();
			return;
		}
	}

	if (prestationsList.value) {
		prestationsList.value.style.opacity = 0;
	}

	setTimeout(() => {
		if (store.getters.locale === "fr_FR_CSE" && company === "cgos") {
			// FIXME this is horrible, who did this?!
			const maxCapacity = parseInt(getCookie("maxCapacity"));
			const filteredPrestations = newPrestations.filter(
				(prestation: Prestation) => {
					if (prestation.maxCapacity) {
						return prestation.maxCapacity <= maxCapacity;
					} else if (prestation.capacity) {
						return prestation.capacity <= maxCapacity;
					} else {
						return true;
					}
				}
			);

			prestations.value = [...filteredPrestations];
		} else {
			prestations.value = [...newPrestations];
		}

		if (prestationsList.value) {
			prestationsList.value.style.opacity = 1;
		}
		setTimeout(() => {
			updateAnimations();
			return;
		}, 100);
	}, 100);
};

const triggerUpdateAnimations = (): void => {
	if (isAnimating.value) return;

	isAnimating.value = true;
	updateAnimations();
	store.state.prestationsLoaded = true;
};

const setIntersectionObserver = (): void => {
	const options = {
		root: null,
		threshold: 0.5,
	};
	const callback = (entries: any) => {
		entries.forEach((entry: any) => {
			if (entry.isIntersecting) {
				if (!store.state.scrolledToPlanning && store.state.prestationsLoaded) {
					trackViewItem(
						store.getters.prestations,
						"view_item",
						store.state.packCode,
						store.state.averageRatingPerLihe,
						store.state.reviewNumberPerLihe
					);
				}
				if (store.state.prestationsLoaded) {
					store.state.scrolledToPlanning = true;
				}
			}
		});
	};
	const observer = new IntersectionObserver(callback, options);
	const target = document.querySelector("#tf-planning");
	if (target) {
		observer.observe(target);
	}
};

const handleSeeMorePrestationsClick = () => {
	if (isShowingAllPrestations.value) {
		additionalPrestationsNum.value = 0;
		scrollToPlannerStart();
	} else {
		additionalPrestationsNum.value += additionalNumberOfPrestationsToShow.value;
	}
};

watch(
	() => store.getters.prestations,
	(newPrestations: Array<Prestation>) => {
		let prestations =
			newPrestations.length > 0 ? newPrestations : getEmptyPrestations();
		queue.value = [...queue.value, prestations];
		if (
			store.state.scrolledToPlanning &&
			newPrestations &&
			newPrestations.length
		) {
			trackViewItem(
				newPrestations,
				"view_item",
				store.state.packCode,
				store.state.averageRatingPerLihe,
				store.state.reviewNumberPerLihe
			);
		}
	}
);

watch(
	() => store.state.prestationsLoaded,
	() => {
		setIntersectionObserver();
	}
);

watch(
	() => store.state.loading,
	(newVal: boolean, oldVal: boolean) => {
		if (newVal && !oldVal) {
			queue.value = [...queue.value, getEmptyPrestations()];
		}
	}
);
watch(
	() => queue.value,
	() => {
		nextTick(triggerUpdateAnimations);
	}
);

onMounted(() => {
	prestations.value = getEmptyPrestations();
});

return { __sfc: true,prestationsList, isAnimating, queue, prestations, additionalPrestationsNum, company, defaultNumberOfPrestationsToShow, additionalNumberOfPrestationsToShow, stepNum, prestationsToShow, isShowingAllPrestations, scrollToPlannerStart, getEmptyPrestations, updateAnimations, triggerUpdateAnimations, setIntersectionObserver, handleSeeMorePrestationsClick, store, PrestationComp, OopsBanner2, BlueBtn }
}

})